export const TERMS_REQUEST_SCOPES = {
  PROJECT_ONLY: "PROJECT_ONLY",
  CUSTOMER_ONLY: "CUSTOMER_ONLY",
  CUSTOMER_AND_PROJECT: "CUSTOMER_AND_PROJECT",
};

export const TERMS_REQUEST_SCOPE_OPTIONS = [
  { value: TERMS_REQUEST_SCOPES.PROJECT_ONLY, label: "This project only" },
  { value: TERMS_REQUEST_SCOPES.CUSTOMER_ONLY, label: "Customer default" },
  { value: TERMS_REQUEST_SCOPES.CUSTOMER_AND_PROJECT, label: "Customer and this project" },
];

const hasUsableValue = (value) =>
  value !== null &&
  value !== undefined &&
  String(value).trim() !== "" &&
  !["undefined", "null"].includes(String(value).trim().toLowerCase());

const resolveIdLike = (value) => {
  if (!value || typeof value !== "object") return "";
  const candidate =
    value.id ??
    value.customerId ??
    value.CustomerId ??
    value.ID ??
    value.value ??
    null;
  return hasUsableValue(candidate) ? String(candidate).trim() : "";
};

export const resolveAttachedCustomerId = (order = {}) => {
  const attachedCustomers = Array.isArray(order?.attachedCustomers)
    ? order.attachedCustomers
    : hasUsableValue(order?.attachedCustomers)
      ? [order.attachedCustomers]
      : [];

  const first = attachedCustomers[0];
  if (hasUsableValue(first) && typeof first !== "object") {
    return String(first).trim();
  }

  return resolveIdLike(first);
};

export const resolveAttachedCustomerName = (order = {}, fallback = "") => {
  const attachedCustomers = Array.isArray(order?.attachedCustomers)
    ? order.attachedCustomers
    : [];
  const first = attachedCustomers[0];
  if (first && typeof first === "object") {
    return (
      first.businessName ||
      first.companyName ||
      first.customerName ||
      first.name ||
      fallback
    );
  }
  return fallback;
};

export const shouldShowTermsRequestButton = ({ isSalesOrderUrl, customerId }) =>
  Boolean(isSalesOrderUrl && hasUsableValue(customerId));

export const buildTermsChangeRequestPayload = ({
  scope,
  requestedTermId,
  orderId,
  reason,
}) => ({
  scope,
  requestedTermId,
  orderId:
    scope === TERMS_REQUEST_SCOPES.CUSTOMER_ONLY
      ? null
      : hasUsableValue(orderId)
        ? String(orderId).trim()
        : null,
  reason: String(reason || "").trim(),
});

export const normalizeTermsRequestStatus = (status) =>
  String(status || "").trim().toUpperCase();

export const isPendingTermsRequest = (request = {}) =>
  normalizeTermsRequestStatus(request.status) === "PENDING";

export const requestAppliesToOrder = (request = {}, orderId) => {
  if (!hasUsableValue(orderId)) return false;
  const normalizedOrderId = String(orderId).trim();
  const requestOrderId = String(
    request.orderId || request.order?.OrderId || request.order?.orderId || ""
  ).trim();
  return Boolean(requestOrderId) && requestOrderId === normalizedOrderId;
};

export const findPendingTermsRequest = ({ requests = [], orderId }) => {
  const pendingRequests = (Array.isArray(requests) ? requests : []).filter(isPendingTermsRequest);
  return (
    pendingRequests.find(
      (request) =>
        request.scope === TERMS_REQUEST_SCOPES.PROJECT_ONLY &&
        requestAppliesToOrder(request, orderId)
    ) ||
    pendingRequests.find(
      (request) =>
        request.scope === TERMS_REQUEST_SCOPES.CUSTOMER_AND_PROJECT &&
        requestAppliesToOrder(request, orderId)
    ) ||
    pendingRequests.find((request) => request.scope === TERMS_REQUEST_SCOPES.CUSTOMER_ONLY) ||
    null
  );
};

export const getPendingTermsRequestLabel = (request = {}) =>
  request?.requestedTerm?.title ||
  request?.requestedTermName ||
  request?.requestedTermId ||
  "Requested terms";
